// Owner chip with inline AM picker. Anyone can assign an unassigned customer;
// managers / AM Management can click an assigned AM to reassign or unassign. Every change is logged (AMHistory).
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { OwnerChip } from "./OwnerChip";
import { toast } from "sonner";
import { UserMinus, History } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TTL = 60000;
const cache = {};
function cached(key, fn) {
  const hit = cache[key];
  if (hit && Date.now() - hit.t < TTL) return hit.p;
  const p = fn(); cache[key] = { t: Date.now(), p }; return p;
}

export const loadCanEditAm = () => cached("can", () => axios.get(`${API}/admin/am-management/can-edit`, { withCredentials: true })
  .then((r) => !!r.data?.can_edit).catch(() => false));

const loadAmNames = (canEdit) => cached(canEdit ? "names-am" : "names", () => (canEdit
  ? axios.get(`${API}/admin/am-management/users`, { withCredentials: true }).then((r) => (r.data?.items || []).map((u) => u.name))
  : axios.get(`${API}/users`, { withCredentials: true }).then((r) => r.data || []).then((us) => us.map((u) => u.name)))
  .then((ns) => Array.from(new Set(ns.filter(Boolean))).sort()).catch(() => []));

export function useCanEditAm() {
  const [can, setCan] = useState(false);
  useEffect(() => { loadCanEditAm().then(setCan); }, []);
  return can;
}

const fmt = (ts) => { try { return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }); } catch { return ""; } };
const W = 224, H = 288;

export function OwnerAssignChip({ personId, primary, size = "xs", onChanged, changed }) {
  const [picking, setPicking] = useState(false);
  const [names, setNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  const canEdit = useCanEditAm();
  const ref = useRef(null);
  const pop = useRef(null);

  useEffect(() => { if (picking) loadAmNames(canEdit).then(setNames); }, [picking, canEdit]);
  useLayoutEffect(() => {
    if (!picking || !ref.current) return;
    const place = () => {
      const r = ref.current.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
      const below = window.innerHeight - r.bottom > H + 8;
      setPos({ left, top: below ? r.bottom + 4 : Math.max(8, r.top - H - 4) });
    };
    place();
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [picking]);
  useEffect(() => {
    if (!picking) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target) && !pop.current?.contains(e.target)) setPicking(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [picking]);

  const assign = async (name) => {
    if (!personId || name === (primary || "")) { setPicking(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/crm/persons/${personId}/account-managers`, { primary_am: name }, { withCredentials: true });
      toast.success(name ? `Account Manager set to ${name}` : "Account Manager removed");
      setPicking(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change Account Manager");
    } finally { setSaving(false); }
  };

  if (!personId || (primary && !canEdit)) return <OwnerChip primary={primary} size={size} />;
  const title = changed?.ts ? `Last changed by ${changed.by || "?"} on ${fmt(changed.ts)}${changed.from ? ` (was ${changed.from})` : ""}` : undefined;

  return (
    <span ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      {primary
        ? <button type="button" onClick={() => setPicking((v) => !v)} title={title || "Click to reassign or unassign"} className="rounded-full hover:ring-1 hover:ring-teal-400/50" data-testid={`owner-edit-${personId}`}><OwnerChip primary={primary} size={size} /></button>
        : <OwnerChip primary={null} size={size} onAssign={() => setPicking((v) => !v)} />}
      {picking && pos && createPortal(
        <div ref={pop} onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: pos.left, top: pos.top, width: W, maxHeight: H }}
          className="z-[400] overflow-y-auto rounded-lg border border-white/12 bg-[#0f1420] shadow-xl p-1" data-testid="owner-assign-popover">
          {changed?.ts && (
            <div className="px-2 py-1.5 text-[10.5px] text-slate-500 border-b border-white/5 mb-1 flex items-start gap-1" data-testid="owner-last-change">
              <History className="w-3 h-3 mt-0.5 shrink-0" /><span>{changed.by || "?"} · {fmt(changed.ts)}{changed.from ? ` · was ${changed.from}` : ""}</span>
            </div>
          )}
          {saving && <div className="px-2 py-1.5 text-[11px] text-slate-400">Saving…</div>}
          {!saving && primary && (
            <button type="button" onClick={() => assign("")} data-testid="owner-unassign"
              className="w-full text-left px-2 py-1.5 rounded text-[12px] text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-1.5"><UserMinus className="w-3.5 h-3.5" />Unassign</button>
          )}
          {!saving && names.length === 0 && <div className="px-2 py-1.5 text-[11px] text-slate-500">Loading…</div>}
          {!saving && names.map((n) => (
            <button key={n} type="button" onClick={() => assign(n)} data-testid={`owner-assign-opt-${n}`}
              className={`w-full text-left px-2 py-1.5 rounded text-[12px] hover:bg-white/8 ${n === primary ? "text-teal-300 font-medium" : "text-slate-200"}`}>
              {n}{n === primary ? " · current" : ""}
            </button>
          ))}
        </div>, document.body)}
    </span>
  );
}

export default OwnerAssignChip;
